/** Karta produktu pro katalog. Náhledová plocha (CardPreview), typový štítek jen u videa, cena. */

import { useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRight, Clock, FileText, Play } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { ProductCard as ProductCardData } from '../lib/api';
import { formatPrice, isFree, subjectGrade, typeBadge } from '../lib/format';
import { DUR, EASE } from '../lib/motion';
import CardPreview from './CardPreview';

/** Stagger vstupu karet (jen prvních pár, ať se výpis nerozjíždí zdlouhavě). */
const STAGGER_S = 0.04;
const STAGGER_MAX = 8;

export default function ProductCard({
  product,
  index = 0,
}: {
  product: ProductCardData;
  index?: number;
}) {
  const reduce = useReducedMotion();
  const isVideo = product.content_type === 'VIDEO';
  const free = isFree(product.price_czk);
  // S4: hover a focus jsou nezávislé; náhled je aktivní, dokud trvá aspoň jeden.
  // Tak `pointerleave` nezhasne náhled při stálém focusu a `blur` naopak.
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const active = hovered || focused;

  return (
    <motion.div
      initial={reduce ? false : { opacity: 0, y: 8 }}
      whileInView={reduce ? undefined : { opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{
        duration: DUR.base,
        ease: EASE,
        delay: index < STAGGER_MAX ? index * STAGGER_S : 0,
      }}
    >
      <Link
        to={`/produkt/${product.slug}`}
        onPointerEnter={() => setHovered(true)}
        onPointerLeave={() => setHovered(false)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        className="group flex h-full flex-col overflow-hidden rounded-card border border-line bg-card shadow-card transition duration-200 ease-out hover:-translate-y-1 hover:border-board/40 hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-board/50 motion-reduce:transform-none motion-reduce:transition-none"
      >
        {/* Čtverec na čtverec: prodejní vizuál (1:1) se vejde celý bez ořezu.
            Třída .board zůstává jako fallback pod glyfem u produktů bez fotek. */}
        <div className="board relative aspect-square overflow-hidden">
          <CardPreview product={product} active={active} />
          {/* Typový chip jen pro video: u PDF ho duplicitně nese meta řádek v těle karty. */}
          {isVideo && (
            <span className="absolute left-3.5 top-3.5 z-[3] inline-flex items-center gap-1 rounded-full border border-chalk/40 bg-board-deep/60 px-2.5 py-1 text-[0.7rem] font-bold uppercase tracking-[0.1em] text-chalk backdrop-blur">
              <Play className="h-3 w-3" strokeWidth={2.5} />
              Video
            </span>
          )}
        </div>

        <div className="flex flex-1 flex-col gap-1.5 p-4 pb-5">
          <p className="flex items-center gap-1.5 text-xs font-semibold text-ink-soft">
            {isVideo ? (
              <Clock className="h-3.5 w-3.5" strokeWidth={2} />
            ) : (
              <FileText className="h-3.5 w-3.5" strokeWidth={2} />
            )}
            {typeBadge(product)}
          </p>
          <h3 className="line-clamp-2 font-serif text-lg leading-snug">
            <span className="bg-gradient-to-r from-accent to-accent bg-[length:0%_2px] bg-left-bottom bg-no-repeat pb-0.5 transition-[background-size] duration-300 group-hover:bg-[length:100%_2px] group-focus-visible:bg-[length:100%_2px]">
              {product.title}
            </span>
          </h3>
          <p className="text-sm text-ink-soft">{subjectGrade(product)}</p>
          <div className="mt-auto flex items-center justify-between border-t border-line pt-3.5">
            <span
              className={
                free ? 'text-lg font-bold text-success' : 'text-lg font-bold tabular-nums text-ink'
              }
            >
              {formatPrice(product.price_czk)}
            </span>
            <span className="grid h-9 w-9 place-items-center rounded-xl bg-accent-soft text-board transition group-hover:bg-accent">
              <ArrowRight
                className="h-[18px] w-[18px] transition-transform group-hover:translate-x-0.5"
                strokeWidth={2.2}
              />
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
